/**
 * Admin → Partner Broadcast (Jun 2026).
 *
 * Route: /admin/partner-broadcast
 *
 * Bulk announcement email to ONBOARDED PWS franchisees only. Leads and
 * prospects are excluded by policy in the backend audience query — this
 * page cannot widen the audience, only view it.
 *
 * Send safety (all re-enforced server-side):
 *   1. mandatory successful test send to the admin's own inbox
 *   2. recipient count typed back to confirm
 *   3. second confirmation above the large-send threshold
 *   4. no partner emailed twice in the same broadcast unless overridden
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Megaphone, Send, Mail, Users, ShieldAlert, AlertTriangle,
  Check, Eye, Trash2, RefreshCw, FileText, History, Ban, Braces, Plus,
  Bold, Italic, List, Link2, Info,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  htmlToBroadcastMarkup, toggleMarker, toggleBullets, insertLink,
  snapOutsideTokens,
} from "@/lib/broadcastMarkup";
import { fetchAndOpenAuthed } from "@/lib/authedDownload";
import { PortalShell } from "@/components/PortalShell";

// Short labels for the recipient summary line, e.g. "Excluded: 2 unsubscribed".
const EXCLUSION_LABELS = {
  unsubscribed: "unsubscribed",
  suppressed: "suppressed",
  no_email: "with no email address",
  invalid_email: "with an invalid email address",
  test_account: "test accounts",
  duplicate_address: "duplicate addresses",
};

const cardCls = "rounded-2xl border border-slate-200 bg-white p-5";
const labelCls =
  "text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block";
const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500";
const btn =
  "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  test_sent: "bg-amber-100 text-amber-800",
  sending: "bg-blue-100 text-blue-800",
  sent: "bg-emerald-100 text-emerald-800",
  partially_sent: "bg-amber-100 text-amber-800",
  failed: "bg-rose-100 text-rose-800",
};

const StatusPill = ({ status }) => (
  <span
    data-testid={`broadcast-status-${status}`}
    className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
      STATUS_TONE[status] || "bg-slate-100 text-slate-700"
    }`}
  >
    {(status || "").replace(/_/g, " ")}
  </span>
);

export default function PartnerBroadcast() {
  const [audience, setAudience] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [confirmCount, setConfirmCount] = useState("");
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [mergeFields, setMergeFields] = useState([]);
  const [mergePreview, setMergePreview] = useState({});
  const [mergePreviewName, setMergePreviewName] = useState("");
  const [bodyCaret, setBodyCaret] = useState(0);
  const [pasteNote, setPasteNote] = useState("");

  /** Apply a markup transform to the body textarea, preserving selection. */
  const applyToBody = useCallback((transform) => {
    const el = document.getElementById("bc-body");
    if (!el) return;
    // Never let formatting cut a {{merge_field}} in half.
    const [start, end] = snapOutsideTokens(
      el.value, el.selectionStart, el.selectionEnd,
    );
    const next = transform(el.value, start, end);
    if (!next) return;
    setBody(next.value);
    setBodyCaret(next.selectionEnd);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  }, []);

  const onBodyKeyDown = useCallback(
    (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "b") {
        e.preventDefault();
        applyToBody((v, s, t) => toggleMarker(v, s, t, "**"));
      } else if (key === "i") {
        e.preventDefault();
        applyToBody((v, s, t) => toggleMarker(v, s, t, "*"));
      } else if (key === "k") {
        e.preventDefault();
        const url = window.prompt("Link URL (https://…)");
        if (url && /^https?:\/\//i.test(url)) {
          applyToBody((v, s, t) => insertLink(v, s, t, url.trim()));
        }
      }
    },
    [applyToBody],
  );

  /** Keep bold/italic/bullets/links when pasting; drop fonts and junk. */
  const onBodyPaste = useCallback(
    (e) => {
      const rich = e.clipboardData?.getData("text/html");
      if (!rich) return; // plain-text paste — let the browser handle it
      const markup = htmlToBroadcastMarkup(rich);
      if (!markup) return;
      e.preventDefault();
      applyToBody((v, s, t) => ({
        value: v.slice(0, s) + markup + v.slice(t),
        selectionStart: s + markup.length,
        selectionEnd: s + markup.length,
      }));
      setPasteNote(
        "Formatting kept: bold, italic, bullets and links. Fonts, colours and sizes were removed so the email renders the same everywhere.",
      );
    },
    [applyToBody],
  );

  const insertMergeField = useCallback(
    (token) => {
      const at = Math.min(Math.max(bodyCaret, 0), body.length);
      const next = `${body.slice(0, at)}${token}${body.slice(at)}`;
      setBody(next);
      setBodyCaret(at + token.length);
      const el = document.getElementById("bc-body");
      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(at + token.length, at + token.length);
        });
      }
    },
    [body, bodyCaret],
  );

  const load = useCallback(async () => {
    try {
      const [a, t, l, m] = await Promise.all([
        api.get("/admin/partner-broadcasts/audience"),
        api.get("/admin/partner-broadcast-templates"),
        api.get("/admin/partner-broadcasts"),
        api.get("/admin/partner-broadcasts/merge-fields"),
      ]);
      setAudience(a.data);
      setTemplates(t.data.templates || []);
      setBroadcasts(l.data.broadcasts || []);
      setMergeFields(m.data.fields || []);
      setMergePreview(m.data.preview_context || {});
      setMergePreviewName(
        m.data.preview_is_sample ? "" : m.data.preview_partner_name || "",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openBroadcast = useCallback(async (id) => {
    try {
      const [b, r] = await Promise.all([
        api.get(`/admin/partner-broadcasts/${id}`),
        api.get(`/admin/partner-broadcasts/${id}/recipients`),
      ]);
      setCurrent(b.data);
      setRecipients(r.data.recipients || []);
      setSubject(b.data.subject);
      setBody(b.data.body);
      setConfirmCount("");
      setConfirmLarge(false);
      setAllowDuplicates(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const count = audience?.recipient_count ?? 0;
  const isLarge = count > (audience?.large_send_threshold ?? 50);
  const locked = ["sending", "sent", "partially_sent", "failed"].includes(
    current?.status,
  );
  const dirty =
    !!current && (subject !== current.subject || body !== current.body);
  const canSend =
    !!current &&
    !locked &&
    !dirty &&
    current.test_send_ok &&
    count > 0 &&
    Number(confirmCount) === count &&
    (!isLarge || confirmLarge);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const createDraft = (tpl) =>
    run("create", async () => {
      const { data } = await api.post("/admin/partner-broadcasts", {
        subject: tpl?.subject || "Partner update",
        body: tpl?.body || "Write your update here.",
      });
      toast.success("Draft created");
      await load();
      await openBroadcast(data.id);
    });

  const saveDraft = () =>
    run("save", async () => {
      const { data } = await api.put(
        `/admin/partner-broadcasts/${current.id}`,
        { subject, body },
      );
      setCurrent({ ...data, audit: current.audit });
      toast.success(
        data.test_send_ok
          ? "Saved"
          : "Saved — content changed, so a fresh test send is required",
      );
      await load();
    });

  const sendTest = () =>
    run("test", async () => {
      const { data } = await api.post(
        `/admin/partner-broadcasts/${current.id}/test`,
      );
      if (data.ok) toast.success(`Test sent to ${data.sent_to}`);
      else toast.error("Test send failed — real send stays locked");
      await openBroadcast(current.id);
      await load();
    });

  const sendReal = () =>
    run("send", async () => {
      const { data } = await api.post(
        `/admin/partner-broadcasts/${current.id}/send`,
        {
          confirm_count: Number(confirmCount),
          confirm_large_send: confirmLarge,
          allow_duplicates: allowDuplicates,
        },
      );
      toast.success(
        `Sent ${data.results.sent} of ${data.results.attempted}` +
          (data.results.failed ? ` · ${data.results.failed} failed` : ""),
      );
      await openBroadcast(current.id);
      await load();
    });

  const deleteDraft = () =>
    run("delete", async () => {
      await api.delete(`/admin/partner-broadcasts/${current.id}`);
      toast.success("Draft deleted");
      setCurrent(null);
      await load();
    });

  const resubscribe = (partnerId, name) =>
    run(`resub-${partnerId}`, async () => {
      const reason = window.prompt(
        `Re-subscribe ${name} to partner marketing updates?\n\nReason (recorded in the audit log):`,
      );
      if (!reason) return;
      await api.post(`/admin/partners/${partnerId}/broadcast-resubscribe`, {
        reason,
      });
      toast.success(`${name} re-subscribed`);
      await load();
    });

  const excluded = audience?.excluded || {};
  const excludedTotal = useMemo(
    () =>
      Object.values(audience?.excluded_counts || {}).reduce(
        (a, b) => a + b,
        0,
      ),
    [audience],
  );

  if (loading) {
    return (
      <PortalShell>
        <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading broadcast tool…
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div className="space-y-6" data-testid="admin-partner-broadcast-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">
            Partner Communications
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
            Partner Broadcast
          </h1>
          <p className="mt-2 text-base text-slate-600 max-w-2xl">
            Send one announcement to every onboarded PWS franchisee, with a
            full record of who received it.
          </p>
        </div>

        {/* Policy banner */}
        <div
          data-testid="broadcast-policy-banner"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <b>Franchisee leads and prospects can never be emailed from here.</b>{" "}
            The audience is locked to PWS franchisees whose partner account
            is <b>active</b> — the same rule the rest of the marketing kit
            uses. Recruitment campaigns stay in the separate CRM and
            invite workflow, so partner communications can't be mixed with
            prospecting.
          </div>
        </div>

        {/* Recipient selector — the audience is a single locked option,
            so a network-wide send never requires ticking partners one by
            one. Prospects/leads are not offered as a choice at all. */}
        <section className={cardCls} data-testid="broadcast-recipient-selector">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-cyan-600" />
            <h2 className="text-base md:text-lg font-semibold text-slate-900">
              Send to
            </h2>
          </div>

          <label
            data-testid="broadcast-recipient-option-all-active"
            className="mt-3 flex items-start gap-3 rounded-xl border-2 border-cyan-500 bg-cyan-50/50 px-4 py-3 cursor-default"
          >
            <input
              type="radio"
              name="broadcast-audience"
              checked
              readOnly
              data-testid="broadcast-recipient-radio-all-active"
              className="mt-1 h-4 w-4 accent-cyan-600"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">
                All active PWS Partners
              </span>
              <span className="block text-xs text-slate-600 mt-0.5">
                Every PWS franchisee with an active partner account.
                Unsubscribed, suppressed and seeded test addresses are
                removed automatically — you never need to tick partners
                individually.
              </span>
            </span>
          </label>

          <p
            data-testid="broadcast-recipient-summary"
            className="mt-3 text-base font-semibold text-slate-900"
          >
            Recipients: {audience?.recipient_count ?? 0} active PWS{" "}
            {audience?.recipient_count === 1 ? "Partner" : "Partners"}
          </p>
          {excludedTotal > 0 && (
            <p
              data-testid="broadcast-recipient-excluded-summary"
              className="mt-1 text-sm text-slate-600"
            >
              Excluded:{" "}
              {Object.entries(audience?.excluded_counts || {})
                .filter(([, n]) => n > 0)
                .map(([reason, n]) => `${n} ${EXCLUSION_LABELS[reason] || reason}`)
                .join(", ")}
            </p>
          )}
          {audience?.recipient_count === 0 && (
            <p
              data-testid="broadcast-recipient-empty-note"
              className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                No onboarded partners are eligible yet, so there is nobody to
                send to. Partners appear here once their status is active or
                registered.
              </span>
            </p>
          )}

          <p className="mt-3 text-xs text-slate-500">
            Franchisee leads and prospects are not available in this tool.
          </p>
        </section>

        {/* Audience */}
        <section className={cardCls} data-testid="broadcast-audience-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-600" />
              <h2 className="text-base md:text-lg font-semibold text-slate-900">
                Audience
              </h2>
            </div>
            <button
              type="button"
              data-testid="broadcast-refresh-audience"
              onClick={load}
              className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-6">
            <div>
              <div className={labelCls}>Will receive this email</div>
              <div
                data-testid="broadcast-recipient-count"
                className="text-4xl font-bold text-slate-900 leading-none"
              >
                {count}
              </div>
            </div>
            <div>
              <div className={labelCls}>Excluded</div>
              <div
                data-testid="broadcast-excluded-total"
                className="text-4xl font-bold text-slate-400 leading-none"
              >
                {excludedTotal}
              </div>
            </div>
          </div>

          <dl
            className="mt-4 grid gap-2 sm:grid-cols-2"
            data-testid="broadcast-exclusion-breakdown"
          >
            {[
              ["unsubscribed", "Unsubscribed from marketing updates"],
              ["suppressed", "On the global email suppression list"],
              ["no_email", "No email address on record"],
              ["invalid_email", "Email address looks invalid"],
              ["test_account", "Seeded test account (never emailed)"],
              ["duplicate_address", "Duplicate address (deduplicated)"],
            ].map(([key, label]) => (
              <div
                key={key}
                data-testid={`broadcast-excluded-${key}`}
                className="flex items-baseline justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2"
              >
                <dt className="text-sm text-slate-600">{label}</dt>
                <dd className="text-sm font-bold text-slate-900 tabular-nums">
                  {audience?.excluded_counts?.[key] ?? 0}
                </dd>
              </div>
            ))}
          </dl>

          {(audience?.recipients || []).length > 0 && (
            <div className="mt-4" data-testid="broadcast-recipient-list">
              <div className={labelCls}>
                Will receive this email — with their merged greeting
              </div>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {audience.recipients.map((p) => (
                  <li
                    key={p.partner_id}
                    data-testid={`broadcast-recipient-${p.partner_id}`}
                    className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm"
                  >
                    <span className="font-semibold text-slate-800">
                      {p.name}
                    </span>
                    <span className="text-slate-500 break-all">{p.email}</span>
                    <span
                      data-testid={`broadcast-recipient-merge-${p.partner_id}`}
                      className="rounded bg-cyan-50 px-2 py-0.5 font-mono text-xs text-cyan-800"
                    >
                      Hi {p.merge?.first_name || "there"},
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(excluded.unsubscribed || []).length > 0 && (
            <div className="mt-4" data-testid="broadcast-unsubscribed-list">
              <div className={labelCls}>
                Unsubscribed partners — will not be emailed
              </div>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {excluded.unsubscribed.map((p) => (
                  <li
                    key={p.partner_id}
                    className="flex items-center justify-between gap-3 px-3 py-2 flex-wrap"
                  >
                    <span className="text-sm text-slate-700">
                      <Ban className="inline w-3.5 h-3.5 text-rose-500 mr-1.5" />
                      {p.name}
                      <span className="text-slate-400"> · {p.email}</span>
                    </span>
                    <button
                      type="button"
                      data-testid={`broadcast-resubscribe-${p.partner_id}`}
                      disabled={busy === `resub-${p.partner_id}`}
                      onClick={() => resubscribe(p.partner_id, p.name)}
                      className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50 !px-2.5 !py-1 !text-xs`}
                    >
                      Re-subscribe
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                Opting out only stops marketing updates. Booking, report,
                payout and account emails keep working. Re-subscribing is a
                deliberate admin action and is written to the audit log.
              </p>
            </div>
          )}

          {(excluded.no_email || []).length > 0 && (
            <p
              className="mt-3 text-xs text-slate-500"
              data-testid="broadcast-no-email-note"
            >
              No email on record:{" "}
              {excluded.no_email.map((p) => p.name).join(", ")}
            </p>
          )}
        </section>

        {/* Compose */}
        <section className={cardCls} data-testid="broadcast-compose-card">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-cyan-600" />
            <h2 className="text-base md:text-lg font-semibold text-slate-900">
              {current ? "Edit broadcast" : "New broadcast"}
            </h2>
            {current && <StatusPill status={current.status} />}
          </div>

          {!current && (
            <div className="mt-4 flex flex-wrap gap-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`broadcast-template-${t.id}`}
                  disabled={busy === "create"}
                  onClick={() => createDraft(t)}
                  className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}
                >
                  <FileText className="w-4 h-4" /> {t.name}
                </button>
              ))}
            </div>
          )}

          {current && (
            <div className="mt-4 space-y-4">
              <div>
                <label className={labelCls} htmlFor="bc-subject">
                  Subject
                </label>
                <input
                  id="bc-subject"
                  data-testid="broadcast-subject-input"
                  className={inputCls}
                  value={subject}
                  disabled={locked}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="bc-body">
                  Body
                </label>
                {/* Formatting toolbar — body only. Email subjects cannot
                    carry formatting in any client, so the subject field
                    stays plain text. No headings: bold is used for
                    section emphasis so every broadcast looks the same. */}
                <div
                  data-testid="broadcast-format-toolbar"
                  className="flex flex-wrap items-center gap-1 rounded-t-lg border border-slate-300 border-b-0 bg-slate-50 px-2 py-1.5"
                >
                  {[
                    ["bold", Bold, "Bold (Ctrl/Cmd+B)",
                      (v, s, t) => toggleMarker(v, s, t, "**")],
                    ["italic", Italic, "Italic (Ctrl/Cmd+I)",
                      (v, s, t) => toggleMarker(v, s, t, "*")],
                    ["bullets", List, "Bullet list", toggleBullets],
                  ].map(([key, Icon, title, fn]) => (
                    <button
                      key={key}
                      type="button"
                      disabled={locked}
                      title={title}
                      aria-label={title}
                      data-testid={`broadcast-format-${key}`}
                      onClick={() => applyToBody(fn)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-700 hover:bg-white hover:text-cyan-700 disabled:opacity-40"
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={locked}
                    title="Insert link (Ctrl/Cmd+K)"
                    aria-label="Insert link"
                    data-testid="broadcast-format-link"
                    onClick={() => {
                      const url = window.prompt("Link URL (https://…)");
                      if (url && /^https?:\/\//i.test(url)) {
                        applyToBody((v, s, t) => insertLink(v, s, t, url.trim()));
                      }
                    }}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-700 hover:bg-white hover:text-cyan-700 disabled:opacity-40"
                  >
                    <Link2 className="w-4 h-4" />
                  </button>
                  <span className="ml-1 text-[11px] text-slate-500">
                    Paste from Word or Docs — bold, italic, bullets and
                    links are kept
                  </span>
                </div>
                <textarea
                  id="bc-body"
                  data-testid="broadcast-body-input"
                  className={`${inputCls} rounded-t-none font-mono text-[13px] leading-relaxed`}
                  rows={14}
                  value={body}
                  disabled={locked}
                  onChange={(e) => setBody(e.target.value)}
                  onPaste={onBodyPaste}
                  onKeyDown={onBodyKeyDown}
                  onSelect={(e) =>
                    setBodyCaret(e.target.selectionStart ?? body.length)
                  }
                  onKeyUp={(e) =>
                    setBodyCaret(e.target.selectionStart ?? body.length)
                  }
                  onClick={(e) =>
                    setBodyCaret(e.target.selectionStart ?? body.length)
                  }
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Blank line starts a new paragraph.{" "}
                  <code className="font-mono">**bold**</code>,{" "}
                  <code className="font-mono">*italic*</code>,{" "}
                  <code className="font-mono">- </code> for a bullet,{" "}
                  <code className="font-mono">[label](https://…)</code> for
                  a link. Bare links are detected automatically.
                </p>
                {pasteNote && (
                  <p
                    data-testid="broadcast-paste-note"
                    className="mt-2 flex items-start gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800"
                  >
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{pasteNote}</span>
                  </p>
                )}
              </div>

              {/* Merge fields helper */}
              <div
                data-testid="broadcast-merge-fields-helper"
                className="rounded-xl border border-slate-200 bg-slate-50/70 p-4"
              >
                <div className="flex items-center gap-2">
                  <Braces className="w-4 h-4 text-cyan-700" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    Merge fields
                  </h3>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  Click to insert. Each partner gets their own value — the
                  preview and test send show the real result, never the
                  raw field.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {mergeFields.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      disabled={locked}
                      data-testid={`broadcast-merge-insert-${f.key}`}
                      onClick={() => insertMergeField(f.token)}
                      title={f.description}
                      className={`${btn} border border-cyan-200 bg-white text-cyan-800 hover:bg-cyan-50 font-mono text-xs disabled:opacity-50`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {f.token}
                    </button>
                  ))}
                </div>
                <ul className="mt-3 space-y-1">
                  {mergeFields.map((f) => (
                    <li
                      key={f.key}
                      data-testid={`broadcast-merge-field-${f.key}`}
                      className="text-xs text-slate-600"
                    >
                      <code className="font-mono text-slate-800">
                        {f.token}
                      </code>{" "}
                      — {f.label}
                      {mergePreview[f.key] ? (
                        <span className="text-slate-500">
                          {" "}
                          · renders as{" "}
                          <b
                            className="text-slate-800"
                            data-testid={`broadcast-merge-preview-${f.key}`}
                          >
                            {mergePreview[f.key]}
                          </b>
                          {mergePreviewName ? ` for ${mergePreviewName}` : ""}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {(current.merge_unsupported || []).length > 0 && (
                  <p
                    data-testid="broadcast-merge-unsupported-warning"
                    className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      Not a supported merge field:{" "}
                      <b>
                        {(current.merge_unsupported || [])
                          .map((k) => `{{${k}}}`)
                          .join(", ")}
                      </b>
                      . It will be removed before sending rather than
                      emailed out, so fix or delete it.
                    </span>
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="broadcast-save-btn"
                  disabled={locked || !dirty || busy === "save"}
                  onClick={saveDraft}
                  className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}
                >
                  {busy === "save" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Save
                </button>
                <button
                  type="button"
                  data-testid="broadcast-preview-link"
                  onClick={() =>
                    fetchAndOpenAuthed({
                      path: `/admin/partner-broadcasts/${current.id}/preview`,
                      mode: "open",
                      loadingMessage: "Opening preview…",
                    })
                  }
                  className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}
                >
                  <Eye className="w-4 h-4" /> Preview
                </button>
                <button
                  type="button"
                  data-testid="broadcast-new-btn"
                  onClick={() => {
                    setCurrent(null);
                    setRecipients([]);
                  }}
                  className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}
                >
                  Start another
                </button>
                {!locked && (
                  <button
                    type="button"
                    data-testid="broadcast-delete-btn"
                    disabled={busy === "delete"}
                    onClick={deleteDraft}
                    className={`${btn} border border-rose-200 text-rose-700 hover:bg-rose-50`}
                  >
                    <Trash2 className="w-4 h-4" /> Delete draft
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Send */}
        {current && (
          <section className={cardCls} data-testid="broadcast-send-card">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-cyan-600" />
              <h2 className="text-base md:text-lg font-semibold text-slate-900">
                Send
              </h2>
            </div>

            {locked ? (
              <div
                className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700"
                data-testid="broadcast-already-sent-note"
              >
                This broadcast has already been sent
                {current.sent_at
                  ? ` on ${new Date(current.sent_at).toLocaleString()}`
                  : ""}{" "}
                by {current.sent_by || "an admin"}.{" "}
                {current.results && (
                  <b>
                    {current.results.sent} sent · {current.results.failed}{" "}
                    failed · {current.results.skipped_duplicate} skipped
                  </b>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {/* Step 1 */}
                <div
                  className="rounded-xl border border-slate-200 p-4"
                  data-testid="broadcast-step-test"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Step 1 — send a test to yourself
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        Required. The real send stays locked until a test
                        lands, and editing the content resets this.
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="broadcast-send-test-btn"
                      disabled={dirty || busy === "test"}
                      onClick={sendTest}
                      className={`${btn} bg-cyan-600 text-white hover:bg-cyan-700`}
                    >
                      {busy === "test" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4" />
                      )}
                      Send test to me
                    </button>
                  </div>
                  {current.test_send_ok && !dirty && (
                    <p
                      className="mt-2 text-sm font-semibold text-emerald-700"
                      data-testid="broadcast-test-ok"
                    >
                      <Check className="inline w-4 h-4 mr-1" />
                      Test sent to {current.test_sent_to} — real send unlocked
                      {current.test_merge_rendered_for ? (
                        <span
                          className="block font-normal text-slate-600"
                          data-testid="broadcast-test-merge-note"
                        >
                          Merge fields were rendered using{" "}
                          {current.test_merge_rendered_for}&rsquo;s details, so
                          the test is a real example of what partners get.
                        </span>
                      ) : null}
                    </p>
                  )}
                  {dirty && (
                    <p
                      className="mt-2 text-sm font-semibold text-amber-700"
                      data-testid="broadcast-unsaved-warning"
                    >
                      <AlertTriangle className="inline w-4 h-4 mr-1" />
                      Save your changes, then send a fresh test
                    </p>
                  )}
                </div>

                {/* Step 2 */}
                <div
                  className="rounded-xl border border-slate-200 p-4"
                  data-testid="broadcast-step-confirm"
                >
                  <div className="text-sm font-semibold text-slate-900">
                    Step 2 — confirm the recipient count
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    This will go to{" "}
                    <b data-testid="broadcast-send-count">{count}</b>{" "}
                    {count === 1 ? "partner" : "partners"}. Type that number to
                    confirm.
                  </p>
                  <input
                    data-testid="broadcast-confirm-count-input"
                    className={`${inputCls} mt-2 max-w-[140px]`}
                    inputMode="numeric"
                    placeholder={String(count)}
                    value={confirmCount}
                    onChange={(e) =>
                      setConfirmCount(e.target.value.replace(/\D/g, ""))
                    }
                  />

                  {isLarge && (
                    <label
                      data-testid="broadcast-large-send-confirm"
                      className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-900"
                    >
                      <input
                        type="checkbox"
                        data-testid="broadcast-large-send-checkbox"
                        className="mt-0.5"
                        checked={confirmLarge}
                        onChange={(e) => setConfirmLarge(e.target.checked)}
                      />
                      <span>
                        This is a large send ({count} recipients, above the
                        threshold of {audience?.large_send_threshold}). I have
                        checked the audience and the content.
                      </span>
                    </label>
                  )}

                  <label className="mt-3 flex items-start gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="broadcast-allow-duplicates-checkbox"
                      className="mt-0.5"
                      checked={allowDuplicates}
                      onChange={(e) => setAllowDuplicates(e.target.checked)}
                    />
                    <span>
                      Allow re-sending to partners who already received this
                      broadcast (off by default)
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  data-testid="broadcast-send-real-btn"
                  disabled={!canSend || busy === "send"}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Send "${subject}" to ${count} partner(s)?\n\nThis cannot be undone.`,
                      )
                    )
                      sendReal();
                  }}
                  className={`${btn} bg-rose-600 text-white hover:bg-rose-700 w-full sm:w-auto`}
                >
                  {busy === "send" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send to {count} {count === 1 ? "partner" : "partners"}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Send log */}
        {current && recipients.length > 0 && (
          <section className={cardCls} data-testid="broadcast-send-log-card">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-cyan-600" />
              <h2 className="text-base md:text-lg font-semibold text-slate-900">
                Send log
              </h2>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3">Partner</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Result</th>
                    <th className="py-2">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recipients.map((r) => (
                    <tr key={r.id} data-testid={`broadcast-log-row-${r.partner_id}`}>
                      <td className="py-2 pr-3 text-slate-900">{r.name}</td>
                      <td className="py-2 pr-3 text-slate-500 break-all">
                        {r.email}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusPill status={r.status} />
                        {r.error && (
                          <span className="ml-2 text-xs text-rose-600">
                            {r.error}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-slate-500 whitespace-nowrap">
                        {new Date(r.at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Audit */}
        {current?.audit?.length > 0 && (
          <section className={cardCls} data-testid="broadcast-audit-card">
            <h2 className="text-base md:text-lg font-semibold text-slate-900">
              Audit trail
            </h2>
            <ul className="mt-3 space-y-1.5 text-sm">
              {current.audit.map((a) => (
                <li key={a.id} className="text-slate-600">
                  <span className="font-semibold text-slate-900">
                    {a.action.replace(/_/g, " ")}
                  </span>
                  {" · "}
                  {a.actor_email || "system"}
                  {" · "}
                  {new Date(a.at).toLocaleString()}
                  {a.detail && (
                    <span className="text-slate-400"> · {a.detail}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* History */}
        <section className={cardCls} data-testid="broadcast-history-card">
          <h2 className="text-base md:text-lg font-semibold text-slate-900">
            All broadcasts
          </h2>
          {broadcasts.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">
              Nothing sent yet. Start from a template above.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100">
              {broadcasts.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-3 py-2.5 flex-wrap"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {b.subject}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(b.created_at).toLocaleString()} ·{" "}
                      {b.sent_count} sent
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={b.status} />
                    <button
                      type="button"
                      data-testid={`broadcast-open-${b.id}`}
                      onClick={() => openBroadcast(b.id)}
                      className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50 !px-2.5 !py-1 !text-xs`}
                    >
                      Open
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </PortalShell>
  );
}
